import React from "react";
import LegalLayout from "./LegalLayout";
import { LINKS } from "../../lib/constants";

export default function CookiePolicy() {
  return (
    <LegalLayout title="Cookie Policy" lastUpdated="July 2026">
      <p>
        This Cookie Policy explains how ProfitMIND Ltd (trading as forecastly) uses cookies and
        similar technologies across our websites — <strong>profitmind.co.uk</strong>,{" "}
        <strong>forecastly.me</strong>, and our related landing pages and booking pages. It should
        be read alongside our <a href="/privacy-policy">Privacy Policy</a>.
      </p>

      <h2>1. What cookies are</h2>
      <p>
        Cookies are small text files stored on your device when you visit a website. They help the
        site remember things like whether you've consented to non-essential cookies, or (in the
        forecastly app) whether you're logged in.
      </p>

      <h2>2. Cookies we use on profitmind.co.uk</h2>
      <p>
        On this marketing website, we use only strictly-necessary cookies and equivalent browser
        storage. We do NOT use marketing or advertising cookies. For visitor analytics we use{" "}
        <strong>Cloudflare Web Analytics</strong> — a privacy-respecting tool that does NOT set
        cookies, does NOT use localStorage, and does NOT track you across sites. It only collects
        aggregated, anonymised data such as page views, top referrers and country (derived from
        your IP, which is never stored). Because it doesn't set any identifier on your device, it
        falls outside PECR cookie-consent requirements.
      </p>

      <h3>Strictly necessary / functional</h3>
      <ul>
        <li><strong>Cookie-consent confirmation</strong> (localStorage) — remembers that you have acknowledged this notice so the banner doesn't show on every page.</li>
        <li><strong>Cloudflare security cookies</strong> — set by our hosting provider Cloudflare to protect the site from bots, DDoS attacks and abuse.</li>
      </ul>

      <h3>Third-party embeds (set only when you interact)</h3>
      <ul>
        <li><strong>TidyCal</strong> — when our booking calendar is embedded on the contact page, TidyCal may set its own cookies to manage your booking session. Governed by TidyCal's own privacy policy.</li>
        <li><strong>Web3Forms</strong> — used to deliver our contact form submissions. Web3Forms may briefly set technical cookies to prevent spam and validate the submission. Governed by Web3Forms' own privacy policy.</li>
        <li><strong>MailerLite</strong> — when you submit our newsletter form (where available), MailerLite may set cookies to manage your subscription. Governed by MailerLite's own privacy policy.</li>
        <li><strong>Social embeds</strong> (LinkedIn, Instagram) — only if you interact with their content from our site.</li>
      </ul>

      <h2>3. Cookies we use inside forecastly</h2>
      <p>
        Inside the forecastly application we use only strictly necessary cookies and equivalent
        browser storage. We do NOT use analytics, marketing, advertising or tracking cookies on the
        application.
      </p>

      <h3>Essential / functional</h3>
      <ul>
        <li><strong>Session token (JWT)</strong> — required to keep you logged in. Without it you would need to log in on every page. Lasts 30 days or until you log out.</li>
        <li><strong>Onboarding draft</strong> (localStorage) — saves your in-progress onboarding wizard so you can come back later. Stored only in your browser; we cannot read it.</li>
        <li><strong>What-If scenarios</strong> (localStorage) — saves up to 3 stress-test scenarios from the dashboard slider. Stored only in your browser.</li>
        <li><strong>Cookie-consent confirmation</strong> (localStorage) — remembers that you have acknowledged this notice so the banner doesn't show on every page.</li>
      </ul>

      <h3>Third-party cookies inside the app</h3>
      <p>
        When you go through the Stripe Checkout page to pay or upgrade, Stripe sets its own cookies
        to detect fraud and remember session state. These are governed by Stripe's own privacy
        policy (<a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a>).
        We do not have access to them.
      </p>

      <h2>4. No analytics or tracking on the app</h2>
      <p>
        The forecastly application does not currently run Google Analytics, Meta Pixel, Hotjar,
        Mixpanel or any equivalent tool. If that changes, we will update this policy AND show you a
        fresh consent banner with the ability to accept or reject non-essential cookies before any
        tracking is set.
      </p>

      <h2>5. How to control cookies</h2>
      <p>
        You can clear cookies and localStorage from your browser settings at any time. Be aware
        that clearing the session token will log you out of forecastly. Most browsers also let
        you block all cookies — if you do this, the forecastly application will not work because
        you cannot stay logged in.
      </p>
      <p>
        On profitmind.co.uk you can withdraw or re-issue your consent at any time by clearing
        localStorage for this site and reloading.
      </p>

      <h2>6. Do Not Track</h2>
      <p>
        Some browsers offer a "Do Not Track" signal. Because there is no shared industry standard,
        our sites do not currently respond to these signals — but we do honour the choices you make
        in our cookie banner.
      </p>

      <h2>7. Updates to this policy</h2>
      <p>
        We may update this Cookie Policy from time to time to reflect changes in technology, law or
        our practices. The "Last updated" date at the top of the page will always reflect the most
        recent revision.
      </p>

      <h2>8. Questions</h2>
      <p>
        Email <a href={`mailto:${LINKS.email}`}>{LINKS.email}</a>. We're happy to walk you through
        what is set and why.
      </p>
    </LegalLayout>
  );
}
